import type { InputHTMLAttributes, ReactNode } from 'react';

type Size = 'sm' | 'md' | 'lg';

interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  helper?: string;
  error?: string;
  leadingIcon?: ReactNode;
  size?: Size;
}

export function Input({
  label,
  helper,
  error,
  leadingIcon,
  size = 'md',
  id,
  className,
  ...rest
}: InputProps) {
  const inputId = id ?? `input-${rest.name ?? Math.random().toString(36).slice(2, 7)}`;
  const describedBy = helper || error ? `${inputId}-helper` : undefined;
  return (
    <div className="form-group">
      {label && (
        <label htmlFor={inputId} className="input-label">
          {label}
          {rest.required && <span className="text-danger-600 ml-1">*</span>}
        </label>
      )}
      <div className="relative">
        {leadingIcon && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-content-tertiary">
            {leadingIcon}
          </span>
        )}
        <input
          id={inputId}
          className={`input input-${size} ${leadingIcon ? 'pl-9' : ''} ${className ?? ''}`.trim()}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          {...rest}
        />
      </div>
      {error ? (
        <p id={describedBy} className="input-error">
          {error}
        </p>
      ) : helper ? (
        <p id={describedBy} className="input-helper">
          {helper}
        </p>
      ) : null}
    </div>
  );
}
